import { useEffect, useRef, useState } from "react";
import { motion, useInView, useAnimation } from "framer-motion";
import techTraining1 from "../../assets/gallery/education/tech-training1.jpg";
import techTraining2 from "../../assets/gallery/education/tech-training2.jpg";
import techTraining3 from "../../assets/gallery/education/tech-training3.jpg";
import techTraining4 from "../../assets/gallery/education/tech-training4.jpg";
import techTraining5 from "../../assets/gallery/education/tech-training5.png";
import classStudents from "../../assets/gallery/education/classstudents.png";
import classPupils from "../../assets/gallery/education/classpupils.jpeg";
import scholarship from "../../assets/gallery/education/scholarship.jpeg";
import healthOutreach1 from "../../assets/gallery/health/health-outreach1.jpeg";
import healthOutreach2 from "../../assets/gallery/health/health-outreach2.jpeg";
import healthOutreach3 from "../../assets/gallery/health/health-outreach3.png";
import health from "../../assets/gallery/health/health.jpeg";
import hospital1 from "../../assets/gallery/health/hospital1.jpeg";
import roadConstruction1 from "../../assets/gallery/community-projects/road-construction1.jpeg";
import roadConstruction2 from "../../assets/gallery/community-projects/road-construction2.jpeg";
import groupEventAsset from "../../assets/gallery/events/groupix.png";
import graduationImage1 from "../../assets/gallery/events/graduation/image1.jpeg";
import scholarshipComplete2026 from "../../assets/gallery/events/SCHOLARSHIP_COMPLETE.jpeg";
import orientation2026 from "../../assets/gallery/events/2026_ORIENTATION.jpeg";
import { 
  FaArrowRight, FaUsers, FaGraduationCap, 
  FaAward, FaCheckCircle, FaQuoteLeft, 
  FaHandshake, FaBuilding, FaHeart, FaNewspaper, FaCalendarAlt, FaMapMarkerAlt
} from "react-icons/fa";
import { Link } from "react-router-dom";
import HeroSection from "../../components/HeroSection";
import AnimatedCounter from "../../components/AnimatedCounter";
import { eventAPI, resolveMediaUrl } from "../../services/api";

// Animated Section Component
const AnimatedSection = ({ children, delay = 0, className = "" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const controls = useAnimation();

    if (isInView) {
      controls.start("visible");
    }

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={controls}
      variants={{
        hidden: { opacity: 0, y: 30 },
        visible: { opacity: 1, y: 0 }
      }}
      transition={{ duration: 0.6, delay }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

export default function Home() {
  const groupEvent = `${groupEventAsset}?v=20260424`;
  const [recentNewsAndEvents, setRecentNewsAndEvents] = useState([]);
  // Create a collage of images for the hero section
  const collageImages = [
    classStudents,
    classPupils,
    techTraining1,
    techTraining2,
    techTraining3,
    techTraining4,
    techTraining5,
    groupEvent,
    healthOutreach1,
    healthOutreach2,
    healthOutreach3,
    roadConstruction1,
    roadConstruction2,
    // Repeat some images to fill the grid
    classStudents,
    classPupils,
    techTraining1,
    techTraining2,
    techTraining3,
    techTraining4,
    techTraining5,
    groupEvent,
    healthOutreach1,
    healthOutreach2
  ];

  useEffect(() => {
    const loadEvents = async () => {
      try {
        const response = await eventAPI.getPublic(6);
        setRecentNewsAndEvents(response.events || []);
      } catch (error) {
        console.error('Failed to load homepage events:', error);
      }
    };
    loadEvents();
  }, []);

  return (
    <div className="w-full bg-white">
      {/* Hero Section with Collage */}
      <HeroSection
        title="Uniting Ojotorians Across North America"
        subtitle="Fostering unity, development, and cultural preservation. Join our community of professionals, students, and leaders making a difference in our hometown and diaspora."
        buttons={[
          { text: "Apply for Scholarship 2026 Batch", link: "/application-instructions", variant: "blue" },
          { text: "Join Our Community", link: "/register", variant: "blue" },
          { text: "Login", link: "/login", variant: "blue" }
        ]}
        collageImages={collageImages}
      />

      {/* About Us Section - Matching FFE.org style with 2x2 button grid */}
      <section className="section bg-white">
        <div className="section-container">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            {/* Left Section - Text and Buttons */}
            <AnimatedSection>
              <div>
                {/* Title with golden line */}
                <h2 
                  className="text-3xl md:text-4xl font-bold mb-3"
                  style={{
                    fontFamily: "'Open Sans', system-ui, sans-serif",
                    fontWeight: 700,
                    color: '#1F2937'
                  }}
                >
                  About Us
                </h2>
                <div 
                  className="mb-6"
                  style={{
                    width: '60px',
                    height: '3px',
                    backgroundColor: '#F59E0B'
                  }}
                ></div>
                
                {/* Body Text */}
                <p 
                  className="text-base leading-relaxed mb-8"
                  style={{
                    fontFamily: "'Open Sans', system-ui, sans-serif",
                    fontWeight: 400,
                    color: '#374151',
                    lineHeight: 1.7
                  }}
                >
                  Ojoto Union North America was founded with the mission to unite Ojotorians residing in the United States and Canada, fostering unity, development, and cultural preservation. We provide comprehensive support through scholarship programs, community networking, health outreach, and development initiatives that connect our diaspora community with our hometown. Apart from our active members who support the community, contributions come from individual donors, corporations, and foundations across North America who believe in our mission of empowering Ojotorians and supporting development in our hometown.
                </p>
                
                {/* 2x2 Button Grid */}
                <div className="grid grid-cols-2 gap-4">
                  <Link 
                    to="/about/story"
                    className="px-6 py-3 font-semibold text-center transition-colors"
                    style={{
                      fontFamily: "'Open Sans', system-ui, sans-serif",
                      fontWeight: 600,
                      backgroundColor: '#F59E0B',
                      color: '#1F2937',
                      borderRadius: '4px'
                    }}
                  >
                    Our Story
                  </Link>
                  <Link 
                    to="/about/approach"
                    className="px-6 py-3 font-semibold text-center text-white transition-colors"
                    style={{
                      fontFamily: "'Open Sans', system-ui, sans-serif",
                      fontWeight: 600,
                      backgroundColor: '#1E40AF',
                      borderRadius: '4px'
                    }}
                  >
                    Our Approach
                  </Link>
                  <Link 
                    to="/about/team"
                    className="px-6 py-3 font-semibold text-center transition-colors"
                    style={{
                      fontFamily: "'Open Sans', system-ui, sans-serif",
                      fontWeight: 600,
                      backgroundColor: '#F59E0B',
                      color: '#1F2937',
                      borderRadius: '4px'
                    }}
                  >
                    Our Team
                  </Link>
                  <Link 
                    to="/about/supporters"
                    className="px-6 py-3 font-semibold text-center text-white transition-colors"
                    style={{
                      fontFamily: "'Open Sans', system-ui, sans-serif",
                      fontWeight: 600,
                      backgroundColor: '#1E40AF',
                      borderRadius: '4px'
                    }}
                  >
                    Our Supporters
                  </Link>
      </div>
      </div>
            </AnimatedSection>

            {/* Right Section - Image */}
            <AnimatedSection delay={0.2}>
              <div className="relative">
                  <img
                    src={graduationImage1}
                    alt="Ojoto Union Community"
                    className="w-full h-auto rounded-lg card-image"
                  style={{
                    objectFit: 'cover',
                    borderRadius: '8px',
                    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.15)'
                  }}
                />
    </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Recent News & Events - Carousel (placed right after About section) */}
      <section className="section bg-gray-50">
        <div className="section-container">
          <AnimatedSection>
            <div className="text-center mb-10">
              <h2
                className="text-3xl md:text-4xl font-bold mb-3"
                style={{
                  fontFamily: "'Open Sans', system-ui, sans-serif",
                  fontWeight: 700,
                  color: "#1F2937"
                }}
              >
                Recent News & Events
              </h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Highlights from our latest scholarship events and community engagements.
              </p>
            </div>
          </AnimatedSection>

          <div className="overflow-x-auto pb-2">
            <div className="flex gap-6 snap-x snap-mandatory min-w-max">
              {(recentNewsAndEvents.length > 0 ? recentNewsAndEvents : [
                {
                  id: 'fallback-1',
                  title: 'Events will appear here once published by admin.',
                  description: 'No events have been published yet.',
                  event_date: new Date().toISOString(),
                  event_location: 'Ojoto Union NA',
                  media_type: 'image',
                  media_url: scholarshipComplete2026,
                  video_url: ''
                }
              ]).map((item, index) => (
                <AnimatedSection key={index} delay={0.1 + index * 0.1} className="snap-start h-full">
                  <div className="w-[340px] md:w-[420px] h-full bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden flex flex-col">
                    {item.media_type === 'video' && item.media_url ? (
                      <video
                        src={resolveMediaUrl(item.media_url)}
                        controls
                        className="w-full h-56 bg-gray-100"
                        style={{ objectFit: "contain", objectPosition: "center" }}
                      />
                    ) : (
                      <img
                        src={resolveMediaUrl(item.media_url || item.banner_url) || orientation2026}
                        alt={item.title}
                        className="w-full h-56 object-contain bg-gray-100"
                        style={{ objectPosition: "center" }}
                      />
                    )}
                    <div className="p-5 flex flex-col flex-1">
                      <h3
                        className="text-xl font-bold text-gray-900 mb-3"
                        style={{
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          minHeight: '3.5rem'
                        }}
                      >
                        {item.title}
                      </h3>
                      <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 mb-3">
                        <span className="inline-flex items-center gap-2">
                          <FaCalendarAlt />
                          {new Date(item.event_date).toLocaleDateString()}
                        </span>
                        <span className="inline-flex items-center gap-2">
                          <FaMapMarkerAlt />
                          {item.event_location || 'Ojoto Union NA'}
                        </span>
                      </div>
                      <p
                        className="text-gray-600 text-sm mb-4"
                        style={{
                          display: '-webkit-box',
                          WebkitLineClamp: 4,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                          minHeight: '5rem'
                        }}
                      >
                        {item.description}
                      </p>
                      <div className="flex flex-wrap gap-3 mt-auto">
                        {typeof item.id === "number" ? (
                          <Link
                            to={`/resources/news-events/${item.id}`}
                            className="text-primary-600 font-semibold hover:text-primary-700 inline-flex items-center gap-2 text-sm"
                          >
                            Read Event Details <FaArrowRight className="text-xs" />
                          </Link>
                        ) : (
                          <Link
                            to="/resources/news-events"
                            className="text-primary-600 font-semibold hover:text-primary-700 inline-flex items-center gap-2 text-sm"
                          >
                            Read Event Details <FaArrowRight className="text-xs" />
                          </Link>
                        )}
                        {item.video_url && (
                          <a
                            href={item.video_url}
                            target="_blank"
                            rel="noreferrer"
                            className="text-blue-700 font-semibold hover:text-blue-800 inline-flex items-center gap-2 text-sm"
                          >
                            Event Video
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                </AnimatedSection>
              ))}
            </div>
          </div>

          <AnimatedSection delay={0.3}>
            <div className="text-center mt-8">
              <Link
                to="/resources/news-events"
                className="inline-flex items-center gap-2 px-6 py-3 font-semibold rounded-md transition-colors"
                style={{
                  backgroundColor: "#1E40AF",
                  color: "#FFFFFF"
                }}
              >
                View All News & Events
                <FaArrowRight />
              </Link>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Animated Counter Section - With Placements, Students, Package, Trusted Parents */}
      <section className="section bg-gray-50">
        <div className="section-container">
          <AnimatedSection>
            <div className="text-center mb-12">
              <h2 
                className="text-2xl md:text-3xl font-bold mb-4"
                style={{
                  fontFamily: "'Open Sans', system-ui, sans-serif",
                  fontWeight: 700,
                  color: '#1F2937'
                }}
              >
                Our Impact
              </h2>
      </div>
          </AnimatedSection>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-7xl mx-auto">
            {[
              { 
                value: 500, 
                suffix: "+", 
                label: "Placements",
                icon: <FaAward className="text-3xl text-[#1E40AF]" />
              },
              { 
                value: 1000, 
                suffix: "+", 
                label: "Students",
                icon: <FaUsers className="text-3xl text-[#1E40AF]" />
              },
              { 
                value: 75, 
                prefix: "$", 
                suffix: "K", 
                label: "Average Package",
                icon: <FaGraduationCap className="text-3xl text-[#1E40AF]" />
              },
              { 
                value: 800, 
                suffix: "+", 
                label: "Trusted Parents",
                icon: <FaHandshake className="text-3xl text-[#1E40AF]" />
              }
            ].map((stat, index) => (
              <AnimatedSection key={index} delay={0.2 + index * 0.1}>
                <div 
                  className="card text-center"
                  style={{
                    minHeight: '200px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <div className="flex justify-center mb-4">
                    {stat.icon}
                  </div>
                  <div 
                    className="text-4xl md:text-5xl font-bold mb-3"
                    style={{
                      fontFamily: "'Open Sans', system-ui, sans-serif",
                      fontWeight: 700,
                      color: '#1E40AF',
                      lineHeight: 1.2
                    }}
                  >
                    <AnimatedCounter 
                      end={stat.value} 
                      prefix={stat.prefix || ""} 
                      suffix={stat.suffix || ""} 
                      duration={2000} 
                    />
                  </div>
                  <p 
                    className="text-base"
                    style={{
                      fontFamily: "'Open Sans', system-ui, sans-serif",
                      fontWeight: 600,
                      color: '#374151',
                      marginBottom: 0
                    }}
                  >
                    {stat.label}
                  </p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* About / What We Do Section */}
      <section className="section bg-white">
        <div className="section-container">
          <AnimatedSection>
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
                What We Do
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                We provide comprehensive support to Ojotorians in North America through education, healthcare, community development, and cultural preservation initiatives.
              </p>
            </div>
          </AnimatedSection>

          <div className="grid md:grid-cols-3 gap-6 max-w-7xl mx-auto">
            {[
              {
                title: "Scholarship Programs",
                description: "Access educational funding opportunities for students and professionals. We connect members with scholarships that support academic excellence.",
                image: scholarship
              },
              {
                title: "Community Networking",
                description: "Connect with Ojotorians across North America. Build meaningful relationships and expand your professional network within our diaspora community.",
                image: groupEvent
              },
              {
                title: "Health Outreach",
                description: "Supporting healthcare initiatives in our hometown. We organize medical missions, health awareness programs, and support hospital development.",
                image: health
              }
            ].map((service, index) => (
              <AnimatedSection key={index} delay={0.2 + index * 0.1}>
                <div 
                  className="card"
                  style={{
                    minHeight: '380px'
                  }}
                >
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-48 object-cover rounded-md mb-4 card-image"
                    style={{
                      marginBottom: '16px',
                      borderRadius: '8px',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)'
                    }}
                  />
                  <h3 
                    className="text-xl font-bold mb-3"
              style={{
                      fontFamily: "'Open Sans', system-ui, sans-serif",
                      fontWeight: 700,
                      color: '#1F2937',
                      lineHeight: 1.3
                    }}
                  >
                    {service.title}
                  </h3>
                  <p 
                    className="text-gray-600 mb-4 flex-grow"
                    style={{
                      fontFamily: "'Open Sans', system-ui, sans-serif",
                      fontWeight: 400,
                      color: '#374151',
                      lineHeight: 1.6
                    }}
                  >
                    {service.description}
                  </p>
                  <div className="flex gap-3 mt-auto">
              <Link
                to="/about"
                      className="text-primary-600 font-medium hover:text-primary-700 inline-flex items-center gap-2"
                      style={{
                        fontFamily: "'Open Sans', system-ui, sans-serif",
                        fontWeight: 600
                      }}
                    >
                      Learn more
                      <FaArrowRight className="text-sm" />
              </Link>
                <Link
                  to="/register"
                      className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 inline-flex items-center gap-2 text-sm font-semibold transition-colors"
                      style={{
                        fontFamily: "'Open Sans', system-ui, sans-serif"
                      }}
                    >
                      Get Started
                      <FaArrowRight className="text-xs" />
                </Link>
              </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Programs / Scholarships Section */}
      <section className="section bg-gray-50">
        <div className="section-container">
          <AnimatedSection>
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
                Our Programs
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Discover opportunities for growth, development, and community engagement.
              </p>
            </div>
          </AnimatedSection>

          <div className="grid md:grid-cols-2 gap-6 max-w-7xl mx-auto">
            {[
              {
                title: "Scholarship Programs",
                description: "Financial aid and educational support for deserving Ojotorian students in North America and our homeland.",
                image: classStudents,
                link: "/programs/employability-skills"
              },
              {
                title: "Mentorship Program",
                description: "One-on-one guidance from experienced professionals to help you navigate your career and personal development journey.",
                image: techTraining2,
                link: "/programs/mentorship"
              }
            ].map((program, index) => (
              <AnimatedSection key={index} delay={0.2 + index * 0.1}>
                <div 
                  className="card"
                  style={{
                    minHeight: '450px'
                  }}
                >
                  <img
                    src={program.image}
                    alt={program.title}
                    className="w-full h-64 object-cover rounded-md mb-4 card-image"
                    style={{
                      marginBottom: '16px',
                      borderRadius: '8px',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)'
                    }}
                  />
                  <h3 
                    className="text-2xl font-bold mb-3"
                    style={{
                      fontFamily: "'Open Sans', system-ui, sans-serif",
                      fontWeight: 700,
                      color: '#1F2937',
                      lineHeight: 1.3
                    }}
                  >
                    {program.title}
                      </h3>
                  <p 
                    className="text-gray-600 mb-6 flex-grow"
                    style={{
                      fontFamily: "'Open Sans', system-ui, sans-serif",
                      fontWeight: 400,
                      color: '#374151',
                      lineHeight: 1.6
                    }}
                  >
                    {program.description}
                  </p>
                  <div className="flex gap-3 mt-auto">
                      <Link
                      to={program.link} 
                      className="btn-primary"
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}
                    >
                      Learn More
                      <FaArrowRight />
                      </Link>
              <Link
                      to="/register" 
                      className="px-4 py-2 border-2 border-primary-600 text-primary-600 rounded-md hover:bg-primary-50 inline-flex items-center gap-2 text-sm font-semibold transition-colors"
              >
                      Apply Now
              </Link>
                  </div>
            </div>
          </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="section bg-white">
        <div className="section-container">
          <AnimatedSection>
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
                How It Works
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Becoming a member is simple. Follow these steps to start your journey with Ojoto Union North America.
              </p>
            </div>
          </AnimatedSection>

          <div className="grid md:grid-cols-4 gap-6 max-w-7xl mx-auto">
            {[
              {
                number: "01",
                title: "Join Our Community",
                description: "Register as a member and become part of the Ojoto Union North America family."
              },
              {
                number: "02",
                title: "Explore Opportunities",
                description: "Discover scholarships, job opportunities, volunteer programs, and community events."
              },
              {
                number: "03",
                title: "Get Involved",
                description: "Participate in community projects, mentorship programs, and development initiatives."
              },
              {
                number: "04",
                title: "Make an Impact",
                description: "Contribute to our mission of unity, development, and cultural preservation."
              }
            ].map((step, index) => (
              <AnimatedSection key={index} delay={0.2 + index * 0.1}>
                <div 
                  className="card text-center"
                  style={{
                    minHeight: '220px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'flex-start'
                  }}
                >
                  <div 
                    className="w-16 h-16 rounded-full flex items-center justify-center text-xl font-bold mx-auto mb-6"
                    style={{
                      backgroundColor: '#1E40AF',
                      color: '#FFFFFF'
                    }}
                  >
                    {step.number}
                      </div>
                  <h3 
                    className="text-lg font-bold mb-4"
                    style={{
                      fontFamily: "'Open Sans', system-ui, sans-serif",
                      fontWeight: 700,
                      color: '#1E40AF',
                      lineHeight: 1.3
                    }}
                  >
                    {step.title}
                      </h3>
                  <p 
                    className="text-sm flex-grow"
                    style={{
                      fontFamily: "'Open Sans', system-ui, sans-serif",
                      fontWeight: 400,
                      color: '#6B7280',
                      lineHeight: 1.6
                    }}
                  >
                    {step.description}
                  </p>
                      </div>
              </AnimatedSection>
            ))}
          </div>

          <AnimatedSection delay={0.6}>
            <div className="text-center mt-12 flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/register" className="btn-primary inline-flex items-center justify-center gap-2">
                Join Us Today
                <FaArrowRight />
              </Link>
              <Link to="/about" className="px-6 py-3 border-2 border-primary-600 text-primary-600 rounded-md hover:bg-primary-50 inline-flex items-center justify-center gap-2 font-semibold transition-colors">
                Learn More About Us
                <FaArrowRight />
              </Link>
              <Link to="/get-involved/donor" className="px-6 py-3 bg-orange-500 text-white rounded-md hover:bg-orange-600 inline-flex items-center justify-center gap-2 font-semibold transition-colors">
                Support Our Mission
                <FaHeart />
              </Link>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Be A Donor Section - Matching FFE.org style exactly */}
      <section className="section bg-white">
        <div className="section-container">
          <AnimatedSection>
            {/* Title with orange underline */}
            <div className="text-center mb-12">
              <h2 
                className="text-3xl md:text-4xl font-bold mb-3"
                style={{
                  fontFamily: "'Open Sans', system-ui, sans-serif",
                  fontWeight: 700,
                  color: '#1F2937'
                }}
              >
                Donate Now
              </h2>
              <div 
                className="mx-auto"
                style={{
                  width: '80px',
                  height: '3px',
                  backgroundColor: '#F59E0B'
                }}
              ></div>
                    </div>
                    
            {/* Two-column layout: Image left, Text right */}
            <div className="grid md:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
              {/* Left: Image */}
              <AnimatedSection delay={0.2}>
                <div className="relative">
                  <img
                    src={scholarship}
                    alt="Ojoto Union Community"
                    className="w-full h-auto rounded-lg card-image"
                    style={{
                      objectFit: 'cover',
                      maxHeight: '500px',
                      borderRadius: '8px',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)'
                    }}
                  />
                  </div>
              </AnimatedSection>

              {/* Right: Text and Button */}
              <AnimatedSection delay={0.3}>
                <div>
                  <h3 
                    className="text-2xl md:text-3xl font-bold mb-6"
                    style={{
                      fontFamily: "'Open Sans', system-ui, sans-serif",
                      fontWeight: 700,
                      color: '#1F2937',
                      lineHeight: 1.3
                    }}
                  >
                    Your Contributions Make A Huge Difference
                  </h3>
                  <p 
                    className="text-base leading-relaxed mb-8"
                    style={{
                      fontFamily: "'Open Sans', system-ui, sans-serif",
                      fontWeight: 400,
                      color: '#374151',
                      lineHeight: 1.7,
                      fontSize: '16px'
                    }}
                  >
                    Ojoto Union selects gifted students who have the potential to rise above the rest. Through a donation, you can turn their dreams of studying beyond their means into a reality. Remember, the more you support these students and their education, the bigger your impact in their lives and community!
                  </p>
                  <Link 
                    to="/get-involved/donor"
                    className="inline-flex items-center gap-2 px-8 py-4 font-bold rounded-md transition-colors"
                    style={{
                      fontFamily: "'Open Sans', system-ui, sans-serif",
                      fontWeight: 700,
                      backgroundColor: '#F59E0B',
                      color: '#1F2937',
                      textDecoration: 'none',
                      fontSize: '16px',
                      letterSpacing: '0.5px'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = '#D97706';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = '#F59E0B';
                    }}
                  >
                    <FaArrowRight />
                    DONATE NOW
                  </Link>
              </div>
              </AnimatedSection>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* News Section - Matching FFE.org "In The News" style exactly */}
      <section 
        className="section"
        style={{
          backgroundColor: '#F3F4F6' // Light blue-grey background
        }}
      >
        <div className="section-container">
          <AnimatedSection>
            <div className="text-center mb-12">
              <h2 
                className="text-3xl md:text-4xl font-bold mb-4"
                style={{
                  fontFamily: "'Open Sans', system-ui, sans-serif",
                  fontWeight: 700,
                  color: '#1F2937'
                }}
              >
                In The News
              </h2>
            </div>
          </AnimatedSection>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {[
              {
                title: "Ojoto Union Reaches 500 Member Milestone",
                description: "We're excited to announce that Ojoto Union North America has reached 500 active members across the United States and Canada, marking a significant milestone in our community growth.",
                image: graduationImage1,
                link: "/resources/news-events"
              },
              {
                title: "New Scholarship Program Launched for STEM Students",
                description: "We've launched a new scholarship program specifically for students pursuing STEM fields, providing financial support and mentorship opportunities.",
                image: techTraining4,
                link: "/resources/news-events"
              },
              {
                title: "Community Development Project Completed in Ojoto",
                description: "Our latest community development project in Ojoto has been successfully completed, improving infrastructure and quality of life for local residents.",
                image: roadConstruction1,
                link: "/resources/news-events"
              }
            ].map((newsItem, index) => (
              <AnimatedSection key={index} delay={0.2 + index * 0.1}>
                <div 
                  className="h-full flex flex-col bg-white rounded-lg overflow-hidden"
                  style={{
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                  }}
                >
                  {/* News Image */}
                  <div className="w-full h-48" style={{ padding: '4px', overflow: 'visible' }}>
                    <img
                      src={newsItem.image}
                      alt={newsItem.title}
                      className="w-full h-full object-cover card-image"
                      style={{
                        borderRadius: '8px',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)',
                        width: 'calc(100% - 8px)',
                        height: 'calc(100% - 8px)'
                      }}
                    />
                  </div>
                  
                  {/* News Content */}
                  <div className="p-6 flex-grow flex flex-col">
                    <h3 
                      className="text-lg font-bold mb-3"
                      style={{
                        fontFamily: "'Open Sans', system-ui, sans-serif",
                        fontWeight: 700,
                        color: '#1F2937',
                        lineHeight: 1.4
                      }}
                    >
                      {newsItem.title}
                    </h3>
                    <p 
                      className="text-sm mb-4 flex-grow"
                      style={{
                        fontFamily: "'Open Sans', system-ui, sans-serif",
                        fontWeight: 400,
                        color: '#374151',
                        lineHeight: 1.6,
                        fontSize: '14px'
                      }}
                    >
                      {newsItem.description}
                    </p>
                    <Link
                      to={newsItem.link}
                      className="text-primary-600 font-semibold hover:text-primary-700 inline-flex items-center gap-1 mt-auto"
                      style={{
                        fontFamily: "'Open Sans', system-ui, sans-serif",
                        fontWeight: 600,
                        fontSize: '14px'
                      }}
                    >
                      Read More »
                    </Link>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>

          {/* READ ALL Button */}
          <AnimatedSection delay={0.5}>
            <div className="text-center mt-10">
              <Link
                to="/resources/news-events"
                className="inline-flex items-center gap-2 px-8 py-3 font-bold rounded-md transition-colors"
                style={{
                  fontFamily: "'Open Sans', system-ui, sans-serif",
                  fontWeight: 700,
                  backgroundColor: '#1E40AF',
                  color: '#FFFFFF',
                  textDecoration: 'none',
                  fontSize: '16px',
                  letterSpacing: '0.5px'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = '#1E3A8A';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = '#1E40AF';
                }}
              >
                <FaArrowRight />
                READ ALL
              </Link>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Corporate Testimonials Section - Matching FFE.org style exactly */}
      <section className="section bg-white">
        <div className="section-container">
          <AnimatedSection>
            <div className="text-center mb-12">
              <h2 
                className="text-3xl md:text-4xl font-bold mb-4"
                style={{
                  fontFamily: "'Open Sans', system-ui, sans-serif",
                  fontWeight: 700,
                  color: '#1F2937'
                }}
              >
                Corporate Testimonials
              </h2>
            </div>
          </AnimatedSection>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {[
              {
                quote: "Ojoto Union's association with our organization is a deep rooted connection with its values and core foundation. With immense sense of satisfaction of our humble contributions to the lives of Ojoto Union scholars, we feel strengthened and charged to do much more in the coming years. Our most proud contribution has been when we hired one of the mentees last year and could provide internships to 4 mentees this year.",
                author: "Chief John Okeke",
                title: "President, Ojoto Union North America",
                image: classPupils
              },
              {
                quote: "The Ojoto Union North America was established to unite Ojotorians across North America, and since then we have been able to transform the lives of hundreds of students. We now have a strong alumni base working in organizations across various industries, which in itself is a testimony to the kind of impact it has made over the years. Ojoto Union has always delivered way above the commitments made. An unflinching passion for transparency and earnestness in action has been the bulwark of its strengths in the field of enabling education through scholarships.",
                author: "Dr. Chioma Nwosu",
                title: "Vice President, Ojoto Union North America",
                image: classStudents
              }
            ].map((testimonial, index) => (
              <AnimatedSection key={index} delay={0.2 + index * 0.1}>
                <div 
                  className="p-8 rounded-lg h-full flex flex-col testimonial-card"
                  style={{
                    backgroundColor: '#F3F4F6', // Light blue-grey
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                  }}
                >
                  {/* Quote Text */}
                  <p 
                    className="mb-6 flex-grow"
                    style={{
                      fontFamily: "'Open Sans', system-ui, sans-serif",
                      fontWeight: 400,
                      color: '#1F2937',
                      lineHeight: 1.7,
                      fontSize: '15px'
                    }}
                  >
                    "{testimonial.quote}"
                  </p>
                  
                  {/* Author Info with Circular Image */}
                  <div className="flex items-center gap-4 pt-4 border-t border-gray-300">
                    <div 
                      className="w-16 h-16 rounded-full overflow-hidden flex-shrink-0"
                      style={{
                        border: '2px solid #E5E7EB'
                      }}
                    >
                      <img
                        src={testimonial.image}
                        alt={testimonial.author}
                        className="w-full h-full object-cover card-image"
                        style={{
                        borderRadius: '50%',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.2)'
                        }}
                      />
                    </div>
                <div>
                      <div 
                        className="font-bold"
                        style={{
                          fontFamily: "'Open Sans', system-ui, sans-serif",
                          fontWeight: 700,
                          color: '#1F2937',
                          fontSize: '16px'
                        }}
                      >
                        {testimonial.author}
                </div>
                      <div 
                        className="text-sm"
                        style={{
                          fontFamily: "'Open Sans', system-ui, sans-serif",
                          fontWeight: 400,
                          color: '#6B7280',
                          fontSize: '14px'
                        }}
                      >
                        {testimonial.title}
              </div>
            </div>
                  </div>
                  </div>
              </AnimatedSection>
            ))}
                </div>
                </div>
      </section>

      {/* Partners Section */}
      <section className="section bg-white">
        <div className="section-container">
          <AnimatedSection>
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
                Our Partners
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                We work with organizations and institutions that share our commitment to community development and education.
                  </p>
                </div>
          </AnimatedSection>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[1, 2, 3, 4].map((item, index) => (
              <AnimatedSection key={index} delay={0.1 + index * 0.1}>
                <div className="card flex items-center justify-center h-32">
                  <FaBuilding className="text-4xl text-gray-400" />
              </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action Section */}
      <section 
        className="section text-center"
        style={{
          backgroundColor: '#F9FAFB'
        }}
      >
        <div className="section-container">
          <AnimatedSection>
            <h2 
              className="text-3xl md:text-4xl font-bold mb-4"
              style={{
                fontFamily: "'Open Sans', system-ui, sans-serif",
                fontWeight: 700,
                color: '#1F2937'
              }}
            >
              Ready to Join Our Community?
            </h2>
            <p 
              className="text-lg max-w-2xl mx-auto mb-8"
              style={{
                fontFamily: "'Open Sans', system-ui, sans-serif",
                fontWeight: 400,
                color: '#374151',
                lineHeight: 1.6
              }}
            >
              Become part of a vibrant community of Ojotorians working together to preserve our culture, support our hometown, and empower each other across North America.
            </p>
            <Link 
              to="/register"
              className="inline-flex items-center justify-center gap-2 px-8 py-3 font-semibold rounded-md transition-colors"
              style={{
                fontFamily: "'Open Sans', system-ui, sans-serif",
                fontWeight: 600,
                backgroundColor: '#1E40AF',
                color: '#FFFFFF',
                textDecoration: 'none',
                borderRadius: '6px'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#1E3A8A';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#1E40AF';
              }}
            >
              Apply Now
              <FaArrowRight />
            </Link>
          </AnimatedSection>
              </div>
      </section>
    </div>
  );
}
